import React, { useState } from 'react';
import { ArrowLeft, MapPin, Calendar, Building, Phone, Mail, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import atriumSezanaImage from '@/assets/atrium-sezana.png';
import atriumRender1 from '@/assets/atrium-render-1.png';
import atriumRender2 from '@/assets/atrium-render-2.jpg';
import atriumRender3 from '@/assets/atrium-render-3.png';
import atriumRender4 from '@/assets/atrium-render-4.jpg';
import atriumRender5 from '@/assets/atrium-render-5.png';

const AtriumSezanaPage = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const images = [atriumRender1, atriumRender2, atriumRender3, atriumRender4, atriumRender5];
  return (
    <div className="min-h-screen">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={atriumSezanaImage}
              alt="Atrium Sežana"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-hero opacity-80"></div>
          </div>

          <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-4xl mx-auto">
              <Button
                asChild
                variant="ghost"
                className="mb-6 text-white hover:bg-white/10"
              >
                <Link to="/projekt/atrium-sezana">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Nazaj
                </Link>
              </Button>
              
              <Badge className="bg-atren-orange/10 text-atren-orange">
                V gradnji
              </Badge>
              
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 mt-4">
                Atrium Sežana
              </h1>
              
              <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto">
                Butična stanovanja v kraškem slogu - kjer tradicija sreča sodobnost
              </p>
              
              <div className="flex flex-wrap items-center justify-center gap-6 text-white/90">
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  Center Sežane
                </div>
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  Zaključek 2026
                </div>
                <div className="flex items-center">
                  <Building className="w-5 h-5 mr-2" />
                  8 stanovanj
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8">
                Zgodba <span className="text-atren-orange">Atriuma</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                V srcu Sežane stoji stara kraška hiša iz kamna, ki nosi v sebi zgodbe preteklih stoletij. 
                Z največjo pozornostjo do detajlov preoblikujemo staro kraško hišo v 8 butičnih stanovanj, 
                kjer se prepletata tradicija in sodobnost.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Vsako stanovanje bo unikatno zasnovano, z ohranjeno kraško arhitekturo in vsemi sodobnimi 
                ugodnostmi za udobno življenje.
              </p>
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section className="py-20 bg-gradient-subtle">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">
              Vizualizacije projekta
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {images.map((image, index) => (
                <div 
                  key={index} 
                  className="rounded-lg overflow-hidden shadow-elegant cursor-pointer"
                  onClick={() => setSelectedImage(image)}
                >
                  <img
                    src={image}
                    alt={`Atrium Sežana vizualizacija ${index + 1}`}
                    className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>

            {/* Lightbox Dialog */}
            <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
              <DialogContent className="max-w-[95vw] max-h-[95vh] w-auto h-auto p-2 border-0 bg-black/90 pointer-events-auto">
                <img
                  src={selectedImage || ''}
                  alt="Atrium Sežana povečana slika"
                  className="max-w-full max-h-[90vh] object-contain rounded-lg"
                />
              </DialogContent>
            </Dialog>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">
                Značilnosti projekta
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[
                  { title: "Kraška tradicija", desc: "Tradicionalni 'borjač', kamnite stopnice, kamnit tlak in suhozidi" },
                  { title: "Celovita prenova", desc: "Prenovljena streha in fasada v skladu z ZVKDjem" },
                  { title: "Sodobne instalacije", desc: "Nove vodovodne, kanalizacijske in električne instalacije" },
                  { title: "Toplotna črpalka", desc: "Ogrevanje s toplotno črpalko in talnim ogrevanjem" },
                  { title: "Klimatske naprave", desc: "Klimatizacija v vseh stanovanjih za celoletno udobje" },
                  { title: "Les-alu okna", desc: "Kvalitetna les-alu okna za odlično izolacijo" },
                  { title: "5 lastniških teras", desc: "Zasebne terase za uživanje na prostem" },
                  { title: "Skupno dvorišče", desc: "Atrium - osrednje dvorišče za druženje stanovalcev" },
                  { title: "Nizki stroški", desc: "Energetsko učinkovita prenova zagotavlja nizke obratovalne stroške" },
                ].map((feature, index) => (
                  <Card key={index} className="border-0 bg-white shadow-elegant">
                    <CardContent className="p-6">
                      <div className="w-3 h-3 bg-atren-orange rounded-full mb-4"></div>
                      <h3 className="font-semibold text-foreground mb-2">{feature.title}</h3>
                      <p className="text-muted-foreground text-sm">{feature.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Price Section */}
        <section className="py-20 bg-gradient-subtle">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Cenovno območje
              </h2>
              <p className="text-5xl md:text-6xl font-bold text-atren-orange mb-4">
                160.000€ - 270.000€
              </p>
              <p className="text-lg text-muted-foreground mb-8">
                8 unikatnih stanovanj • 4 v pritličju • 4 v nadstropju
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  asChild
                  size="lg"
                  className="bg-gradient-primary shadow-primary hover:shadow-elegant transition-all duration-300"
                >
                  <a href="/#kontakt">
                    <Phone className="w-4 h-4 mr-2" />
                    Rezervirajte termin ogleda
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-atren-orange text-atren-orange hover:bg-atren-orange hover:text-white"
                >
                  <a href="mailto:info@atren.eu">
                    <Mail className="w-4 h-4 mr-2" />
                    info@atren.eu
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AtriumSezanaPage;
